import React from 'react';

export interface NumericInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> {
  /** 현재 값 (빈 문자열 허용, 백스페이스로 지울 수 있음) */
  value: string;
  /** 값 변경. 빈 문자열이면 아직 비어 있는 상태 */
  onChange: (value: string) => void;
  /** 최소값 (blur 시 빈값을 이 값으로 채울 때도 사용하지 않음, defaultEmptyValue 사용) */
  min?: number;
  max?: number;
  /** 입력 허용 자릿수 (기본 제한 없음) */
  maxLength?: number;
  /** blur 시 비어 있으면 이 값으로 설정 (기본 '0') */
  defaultEmptyValue?: string;
  /** 포커스 시 기존 값 전체 선택 — 클릭 후 입력하면 숫자가 교체됨 */
  selectAllOnFocus?: boolean;
}

/**
 * 숫자 전용 입력 필드 공통 동작:
 * - 0을 백스페이스로 지울 수 있음 (빈 문자열 허용)
 * - blur 시 비어 있으면 defaultEmptyValue(기본 '0')로 설정
 * - 숫자 외 문자는 입력 시 무시
 */
const NumericInput: React.FC<NumericInputProps> = ({
  value,
  onChange,
  min,
  max,
  maxLength = 20,
  defaultEmptyValue = '0',
  selectAllOnFocus = false,
  onBlur,
  onFocus,
  onClick,
  ...rest
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const digitsOnly = raw.replace(/\D/g, '');
    const limited = maxLength ? digitsOnly.slice(0, maxLength) : digitsOnly;
    onChange(limited);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (value === '') {
      onChange(defaultEmptyValue);
    }
    onBlur?.(e);
  };

  const selectAll = (input: HTMLInputElement) => {
    try {
      input.select();
    } catch {
      // ignore
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (selectAllOnFocus) {
      const input = e.currentTarget;
      selectAll(input);
      requestAnimationFrame(() => selectAll(input));
    }
    onFocus?.(e);
  };

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    if (selectAllOnFocus) {
      const input = e.currentTarget;
      selectAll(input);
      requestAnimationFrame(() => selectAll(input));
    }
    onClick?.(e);
  };

  return (
    <input
      type="number"
      inputMode="numeric"
      {...rest}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onClick={handleClick}
      min={min}
      max={max}
    />
  );
};

export default NumericInput;
